// Subscription Service - API calls for subscription and payment management

import { useAuthStore } from "../stores/authStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

const API_BASE = import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// =============================================================================
// Types
// =============================================================================

export type SubscriptionStatus =
  | "none"
  | "active"
  | "expiring"
  | "renewing"
  | "payment_failed"
  | "suspended"
  | "canceled"
  | "paused"
  | "comped";

export type PaymentMethodType = "card" | "us_bank_account";

export type PaymentTransactionStatus = "pending" | "succeeded" | "failed" | "refunded";

export interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  price_cents: number;
  price_formatted: string;
  billing_period: string;
  billing_interval: number;
  features: string[];
}

export interface SubscriptionCheckResult {
  has_access: boolean;
  reason: string;
  status: SubscriptionStatus;
  is_admin: boolean;
  is_integration_test: boolean;
  is_comped: boolean;
  in_grace_period: boolean;
  grace_period_ends_at?: string;
  subscription_ends_at?: string;
}

export interface SubscriptionResponse {
  id: string;
  plan_id: string;
  status: SubscriptionStatus;
  amount_paid_cents?: number;
  amount_paid_display?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  canceled_at?: string;
  created_at: string;
}

export interface PaymentMethod {
  id: string;
  type: PaymentMethodType;
  card_brand?: string;
  card_last4?: string;
  card_expiry?: string;
  bank_name?: string;
  bank_last4?: string;
  is_default: boolean;
  created_at: string;
}

export interface PaymentHistoryItem {
  id: string;
  amount_cents: number;
  amount_display: string;
  currency: string;
  status: PaymentTransactionStatus;
  type: string;
  failure_message?: string;
  receipt_url?: string;
  invoice_pdf_url?: string;
  created_at: string;
}

export interface ReferralCode {
  code: string;
  discount_percent: number;
  times_used: number;
  max_uses?: number;
  is_active: boolean;
  created_at: string;
}

export interface ReferralDetail {
  referred_email: string;
  status: string;
  credit_amount_cents?: number;
  created_at: string;
}

export interface ReferralCredits {
  pending_credits: number;
  applied_credits: number;
  total_referrals: number;
  referrals: ReferralDetail[];
}

export interface CreateSubscriptionRequest {
  plan_id: string;
  payment_method_id: string;
  referral_code?: string;
}

export interface AddPaymentMethodRequest {
  payment_method_id: string;
  set_as_default?: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));

    if (response.status === 401) {
      console.warn("[subscriptionService] 401 Unauthorized - triggering session expired modal");
      useSessionExpiredStore.getState().setExpired();
    }

    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// =============================================================================
// Subscription Status & Access
// =============================================================================

export async function getSubscriptionStatus(): Promise<SubscriptionCheckResult> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/status`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<SubscriptionCheckResult>(response);
}

export async function getSubscription(): Promise<SubscriptionResponse> {
  const response = await fetch(`${API_BASE}/api/v1/subscription`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<SubscriptionResponse>(response);
}

// =============================================================================
// Subscription Management
// =============================================================================

export async function createSubscription(
  request: CreateSubscriptionRequest
): Promise<SubscriptionResponse> {
  const response = await fetch(`${API_BASE}/api/v1/subscription`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<SubscriptionResponse>(response);
}

export async function createCheckoutSession(referralCode?: string, promoCode?: string): Promise<string> {
  const body: Record<string, string> = {};
  if (referralCode) body.referral_code = referralCode;
  if (promoCode) body.promo_code = promoCode;

  const response = await fetch(`${API_BASE}/api/v1/subscription/create-checkout-session`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await handleResponse<{ url: string }>(response);
  return data.url;
}

export async function verifyCheckoutSession(sessionId: string): Promise<SubscriptionResponse> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/verify-checkout`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ session_id: sessionId }),
  });
  return handleResponse<SubscriptionResponse>(response);
}

export async function syncSubscription(): Promise<SubscriptionResponse> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/sync`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse<SubscriptionResponse>(response);
}

export async function createBillingPortalSession(): Promise<string> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/billing-portal`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<{ url: string }>(response);
  return data.url;
}

export async function cancelSubscription(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/v1/subscription`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse<{ message: string }>(response);
}

export async function reactivateSubscription(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/reactivate`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse<{ message: string }>(response);
}

// =============================================================================
// Plans
// =============================================================================

export async function getPlans(): Promise<SubscriptionPlan[]> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/plans`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<SubscriptionPlan[]>(response);
}

// =============================================================================
// Payment Methods
// =============================================================================

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/payment-methods`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<PaymentMethod[]>(response);
}

export async function addPaymentMethod(
  request: AddPaymentMethodRequest
): Promise<PaymentMethod> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/payment-methods`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<PaymentMethod>(response);
}

export async function setDefaultPaymentMethod(methodId: string): Promise<{ message: string }> {
  const response = await fetch(
    `${API_BASE}/api/v1/subscription/payment-methods/${methodId}/default`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
    }
  );
  return handleResponse<{ message: string }>(response);
}

export async function deletePaymentMethod(methodId: string): Promise<{ message: string }> {
  const response = await fetch(
    `${API_BASE}/api/v1/subscription/payment-methods/${methodId}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );
  return handleResponse<{ message: string }>(response);
}

// =============================================================================
// Payment History
// =============================================================================

export async function getPaymentHistory(): Promise<PaymentHistoryItem[]> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/payments`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<PaymentHistoryItem[]>(response);
}

// =============================================================================
// Referrals
// =============================================================================

export async function getReferralCode(): Promise<ReferralCode> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/referral-code`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<ReferralCode>(response);
}

export async function regenerateReferralCode(): Promise<ReferralCode> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/referral-code/regenerate`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse<ReferralCode>(response);
}

export async function getReferralCredits(): Promise<ReferralCredits> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/referral-credits`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<ReferralCredits>(response);
}

// =============================================================================
// Promotions
// =============================================================================

export interface Promotion {
  id: string;
  code: string;
  description: string;
  discount_percent: string;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  is_expired: boolean;
}

export async function getActivePromotions(): Promise<Promotion[]> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/promotions`, {
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<{ promotions: Promotion[] }>(response);
  return data.promotions;
}

// =============================================================================
// Agreement / E-Sign
// =============================================================================

export interface AgreementStatusResult {
  required: boolean;
  signed: boolean;
}

export interface CreateAgreementResult {
  signing_url?: string;
  envelope_id?: string;
  agreement_id?: string;
  already_signed?: boolean;
  message?: string;
}

export interface CompleteAgreementResult {
  signed: boolean;
  message: string;
  event?: string;
}

export async function getAgreementStatus(): Promise<AgreementStatusResult> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/agreement/status`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<AgreementStatusResult>(response);
}

export async function createAgreement(returnUrl?: string): Promise<CreateAgreementResult> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/agreement`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ return_url: returnUrl || "" }),
  });
  return handleResponse<CreateAgreementResult>(response);
}

export async function completeAgreement(
  envelopeId: string,
  event: string
): Promise<CompleteAgreementResult> {
  const response = await fetch(`${API_BASE}/api/v1/subscription/agreement/complete`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ envelope_id: envelopeId, event }),
  });
  return handleResponse<CompleteAgreementResult>(response);
}

// =============================================================================
// Utility Functions
// =============================================================================

/** Returns true if the subscription status allows trading */
export function isActiveSubscription(status: SubscriptionStatus): boolean {
  return ["active", "expiring", "renewing", "comped"].includes(status);
}

/** Format cents to dollar display */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Get a human-readable label for subscription status */
export function getStatusLabel(status: SubscriptionStatus): string {
  const labels: Record<SubscriptionStatus, string> = {
    none: "No Subscription",
    active: "Active",
    expiring: "Expiring",
    renewing: "Renewing",
    payment_failed: "Payment Failed",
    suspended: "Suspended",
    canceled: "Canceled",
    paused: "Paused",
    comped: "Complimentary",
  };
  return labels[status] || status;
}

/** Get a Tailwind color class for subscription status */
export function getStatusColor(status: SubscriptionStatus): string {
  const colors: Record<SubscriptionStatus, string> = {
    none: "text-surface-400",
    active: "text-green-400",
    expiring: "text-yellow-400",
    renewing: "text-blue-400",
    payment_failed: "text-red-400",
    suspended: "text-red-500",
    canceled: "text-surface-500",
    paused: "text-orange-400",
    comped: "text-purple-400",
  };
  return colors[status] || "text-surface-400";
}

/** Get a badge variant for subscription status */
export function getStatusBadgeClasses(status: SubscriptionStatus): string {
  const classes: Record<SubscriptionStatus, string> = {
    none: "bg-surface-700/50 text-surface-300 border-surface-600/50",
    active: "bg-green-500/15 text-green-400 border-green-500/30",
    expiring: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    renewing: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    payment_failed: "bg-red-500/15 text-red-400 border-red-500/30",
    suspended: "bg-red-500/20 text-red-500 border-red-500/40",
    canceled: "bg-surface-700/50 text-surface-400 border-surface-600/50",
    paused: "bg-orange-500/15 text-orange-400 border-orange-500/30",
    comped: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  };
  return classes[status] || "bg-surface-700/50 text-surface-300 border-surface-600/50";
}
